import axios from 'axios';
import { WORKFLOW_TYPE } from '../Constant';

// Compile regex một lần duy nhất (performance optimization)
const TEMPLATE_REGEX = /\${(.*?)}/g;
const ABSOLUTE_URL_REGEX = /^https?:\/\//i;

// Cache constants để tránh toLowerCase() lặp lại
const HTTP_METHODS = {
  POST: 'post',
  PUT: 'put',
  DELETE: 'delete',
} as const;

function wfAxiosGenerator(baseEndpoint : string, configString: string) {
  function convertToJSON(configString : string) {
    try {
      const jsonObject = JSON.parse(configString);
      return jsonObject;
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return null;
    }
  }

  const getValueFromTemplate = (keyStr: string, props: any, defaultValue: any = '') => {
    return keyStr.split('.').reduce((acc, currKey) => acc?.[currKey], props) ?? defaultValue;
  };

  const fillTemplate = (templates: any, props: any) => {
    const result: Record<string, any> = {};

    templates?.forEach((template: any) => {
      const [key, value] = template.includes('=') ? 
        template.split('=') : 
        template.split('-');

      result[key] = template.includes('=') ? value : getValueFromTemplate(value, props);
    });

    return result;
  };

  const replaceTemplateString = (template: string, data: any) => {
    return template.replace(TEMPLATE_REGEX, (_: any, key: any) => {
      const value = getValueFromTemplate(key, data);
      return value !== undefined  ? JSON.stringify(value) : '';
    });
  };

  const replaceTemplateValues = (template: any, data: any): any => {
    if (typeof template === 'string') {
      return template.includes('${')
        ? JSON.parse(replaceTemplateString(template, data))
        : template;
    }
    if (Array.isArray(template)) {
      return template.map(item => replaceTemplateValues(item, data));
    }
    if (typeof template === 'object' && template !== null) {
      return Object.fromEntries(
        Object.entries(template).map(([key, value]) => [
          key,
          replaceTemplateValues(value, data),
        ])
      );
    }
    return template;
  };

  const replaceUrlTemplates = (template: any, data: any) => {
    return template.replace(TEMPLATE_REGEX, (_: any, key: any) => {
      const value = getValueFromTemplate(key, data);
      return value !== undefined ? value : '';
    });
  };

  // Helper function để kiểm tra URL đã là absolute URL chưa
  const isAbsoluteUrl = (url: string): boolean => {
    return ABSOLUTE_URL_REGEX.test(url);
  };

  // Helper function để build URL đầy đủ
  const buildFullUrl = (url: string): string => {
    return isAbsoluteUrl(url) ? url : `${baseEndpoint}${url}`;
  };

  const generateAxiosRequest = (buttonConfig: any) => {
    if (buttonConfig.actionType === WORKFLOW_TYPE.POPUP) {
      return buttonConfig;
    }
    if (buttonConfig.actionType === WORKFLOW_TYPE.SERVICE_TASK) {
      return buttonConfig;
    }

    const {
      code,
      name,
      method,
      url,
      payloadTemplate,
      requestParameters,
      pathVariables,
      actionType,
      action,
    } = buttonConfig;

    const handleClick = async (props:any) => {
      try {
        const payload = Array.isArray(payloadTemplate)
          ? fillTemplate(payloadTemplate, props)
          : replaceTemplateValues(payloadTemplate, props);
        const params = replaceTemplateValues(requestParameters, props);
        let finalUrl = url;

        if (pathVariables) {
          for (const pathInfo of pathVariables) {
            const pathKey = pathInfo.split('-');
            finalUrl = finalUrl.replace(`{${pathKey[0]}}`, getValueFromTemplate(pathKey[1], props));
          }
        } else {
          finalUrl = replaceUrlTemplates(url, props);
        }

        if (actionType === WORKFLOW_TYPE.PRINT) {
          return {
            payload: payload ?? params,
            request: async (extraPayload?: any) =>
              axios({
                method: method ?? action,
                url: buildFullUrl(finalUrl),
                data:{
                  ...payload,
                  ...extraPayload,
                },
                params,
                responseType: 'blob',
          }),
          };
        }

        const methodLower = typeof method === 'string' ? method.toLowerCase() : '';

        if (methodLower === HTTP_METHODS.POST) {
          await axios.post(buildFullUrl(finalUrl), payload, {
            params,
          });
        }
        if (methodLower === HTTP_METHODS.PUT) {
          await axios.put(buildFullUrl(finalUrl), payload, { params });
        }

        if (buttonConfig.actions) {
          const lstActions = generateActions(buttonConfig);
          const actions = lstActions?.[code];
          if (actions && Array.isArray(actions)) {
            for (const action of actions) {
              try {
                await action(props);
              } catch (error) {
                console.error(`[${code}] Action failed:`, error);
                // Continue with next action even if one fails
              }
            }
          }
        }
        
      } catch (error) {
        console.error(`[${code}] ${name} - Error:`, error);
      }
    };
   
    return {
      ...buttonConfig,
      handleClick,
	  };
  };

  const generateActions = (actionsConfig: any) => {
    let { code, actions, actionType } = actionsConfig;

    try {
      actions = actions
        .filter((action: any) => {
          const itemActionType = actionType || action.actionType || 'fetch';
          return itemActionType !== 'service_task';
        })
        .map((action: any) => {
          const { method, url, payloadTemplate, pathVariables, requestParameters, headers } = action;

          const handleClick = async (wfProps: any, callbackData?: any, callbackFunc?: any) => {
            let response: any = null;
            let props = { ...wfProps, ...wfProps?.additionalProps };
            try {
              let finalUrl = url;

              let payload = Array.isArray(payloadTemplate) ? fillTemplate(payloadTemplate, props) : replaceTemplateValues(payloadTemplate, props);
              const params = replaceTemplateValues(requestParameters, props);
              const processedHeaders = replaceTemplateValues(headers, props);

              if (pathVariables) {
                for (const pathInfo of pathVariables) {
                  const pathKey = pathInfo.split('-');
                  finalUrl = finalUrl.replace(`{${pathKey[0]}}`, getValueFromTemplate(pathKey[1], props));
                }
              } else {
                finalUrl = replaceUrlTemplates(url, props);
              }

              if (callbackData) {
                if (payload) {
                  payload.allowDisableRules = callbackData?.allowDisableRules;
                } else {
                  payload = { allowDisableRules: callbackData?.allowDisableRules };
                }
              };

              const methodLower = typeof method === 'string' ? method.toLowerCase() : '';
              const axiosConfig: any = {
                params,
                callback: callbackFunc
              };

              // Add headers if provided
              if (processedHeaders && Object.keys(processedHeaders).length > 0) {
                axiosConfig.headers = processedHeaders;
              }

              if (methodLower === HTTP_METHODS.POST) {
                response = await axios.post(buildFullUrl(finalUrl), payload, axiosConfig);
              } else if (methodLower === HTTP_METHODS.PUT) {
                response = await axios.put(buildFullUrl(finalUrl), payload, axiosConfig);
              } else if (methodLower === HTTP_METHODS.DELETE) {
                response = await axios.delete(buildFullUrl(finalUrl), axiosConfig);
              }
              return response;
            } catch (error) {
              console.error(`[${code}] - Error:`, error);
              throw new Error(`Error: [${code}]`);
            }
          };

          return handleClick;
        });

      return { [code]: actions };
    } catch (error) {
      console.error(error);
    }
  };  

  const config = convertToJSON(configString);

  if (config) {
    const axiosRequests = config.additionalButton?.map(generateAxiosRequest);
    const actions: Record<string, any> = {};
    const componentName = config.componentName || "";
    const components = config.components || "";
    const icon = config.icon || "";
    const type = config.type || "";
    const componentNameNewReq = config.componentNameNewReq || "";
    const iconText = config.iconText || "";
    const models = config.models || "";
    const priority = config.priority || "";

    config.additionalAction?.forEach((actionConfig: any) => {
      const action = generateActions(actionConfig);
      if (action) {
        Object.assign(actions, action);
      }
    });

    return { axiosRequests, actions, componentName, icon, type, componentNameNewReq, iconText, components, models, priority };
  }
}

export default wfAxiosGenerator; 